import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, KeyRound, Loader2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { portalPath } from '../../lib/portal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GovForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [debugToken, setDebugToken] = useState('');   // dev convenience — surfaced in preview only

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      if (res.data?.reset_token) setDebugToken(res.data.reset_token);
    } catch (e) { /* same-response anti-enumeration */ }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 flex items-center justify-center px-4 py-10" data-testid="gov-forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 text-white">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-lg bg-white text-blue-900 mb-3"><KeyRound className="h-7 w-7" /></div>
          <h1 className="text-2xl font-bold">Reset your password</h1>
          <p className="text-blue-200 text-sm mt-1">USKE Procurement Portal</p>
        </div>
        <Card className="border-blue-100">
          <CardContent className="p-6 sm:p-8">
            {sent ? (
              <div className="text-center space-y-3">
                <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
                <h2 className="text-lg font-semibold text-slate-800">Check your email</h2>
                <p className="text-sm text-slate-500">
                  If an account exists for <b>{email}</b>, we&apos;ve emailed a reset link valid for 1 hour.
                </p>
                {debugToken && (
                  <div className="text-[10px] bg-amber-50 border border-amber-200 rounded p-2 break-all text-left">
                    <strong>Dev only — reset link:</strong><br />
                    <Link to={`${portalPath('/reset-password')}?token=${debugToken}`} className="text-blue-700 underline" data-testid="gov-forgot-dev-link">
                      {portalPath(`/reset-password?token=${debugToken}`)}
                    </Link>
                  </div>
                )}
                <Link to={portalPath('/login')} className="text-sm text-blue-700 underline">← Back to sign in</Link>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-slate-800 mb-3">Forgot your password?</h2>
                <p className="text-sm text-slate-500 mb-4">Enter your email and we&apos;ll send you a reset link.</p>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label htmlFor="em">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input id="em" type="email" required className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="gov-forgot-email" />
                    </div>
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-blue-700 hover:bg-blue-800 text-white" data-testid="gov-forgot-submit">
                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : 'Send reset link'}
                  </Button>
                </form>
                <div className="mt-5 pt-5 border-t text-center text-xs text-slate-500">
                  Remembered it? <Link to={portalPath('/login')} className="text-blue-700 underline">Sign in</Link>.
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GovForgotPassword;
